/**
 * Types for Matchday Planning (Spieltagsplanung)
 */

export type AnchorType = 'departure' | 'arrival' | 'stopover' | 'free' | 'main';
export type VehicleType = 'bus' | 'minibus' | 'car' | 'plane' | 'train' | 'taxi' | 'rentalcar' | 'ship' | 'bicycle' | 'onfoot';
export type TripType = 'team' | 'empty';
export type PlanningStatus = 'draft' | 'inProgress' | 'confirmed' | 'completed' | 'cancelled';
export type DriverType = 'traveler' | 'partnerDriver';

/**
 * Main planning entity
 */
export interface MatchdayPlanning {
  id: number;
  name: string;
  date: string;
  kickoffTime: string;
  arrivalLeadMinutes: number;
  status: PlanningStatus;
  localMatchdayId?: number;
  matchId?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  segments: PlanningSegment[];
  vehicleAssignments?: VehicleAssignment[];
  staffAssignments?: StaffAssignment[];

  // Related entity information
  localMatchday?: LocalMatchdayInfo;
  match?: MatchInfo;
}

/**
 * Individual segment in the planning timeline.
 * Driver/vehicle assignment is done via VehicleAssignment (vehicleAssignmentId FK).
 */
export interface PlanningSegment {
  id: number;
  planningId: number;
  sequenceOrder: number;
  location: string;
  description?: string;
  scheduledDate?: string;
  scheduledTime: string;
  anchorType: AnchorType;
  vehicleType: VehicleType;
  tripType: TripType;
  stopoverDurationMinutes?: number;
  estimatedDistanceKm?: number;
  estimatedDurationMinutes?: number;
  passengerCount?: number;
  co2eKg?: number;
  isMainEvent?: boolean;
  vehicleAssignmentId?: number;
  clubId?: number;
  customLocation?: string;
  createdAt: string;
  updatedAt: string;

  // Related entity information
  club?: ClubInfo;
  vehicleAssignment?: VehicleAssignment;
}

/**
 * Request/Response types for API
 */
export interface CreatePlanningRequest {
  name: string;
  date: string;
  kickoffTime: string;
  arrivalLeadMinutes: number;
  localMatchdayId?: number;
  matchId?: number;
  notes?: string;
}

export interface CreateSegmentRequest {
  planningId: number;
  location: string;
  description?: string;
  scheduledDate?: string;
  scheduledTime: string;
  anchorType: AnchorType;
  vehicleType: VehicleType;
  tripType: TripType;
  stopoverDurationMinutes?: number;
  passengerCount?: number;
  insertPosition: 'before-game' | 'after-game';
  vehicleAssignmentId?: number;
  clubId?: number;
  customLocation?: string;
}

export interface UpdateSegmentRequest {
  id: number;
  location?: string;
  description?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  anchorType?: AnchorType;
  vehicleType?: VehicleType;
  tripType?: TripType;
  stopoverDurationMinutes?: number;
  passengerCount?: number;
  vehicleAssignmentId?: number;
  clubId?: number;
  customLocation?: string;
}

/**
 * UI State types
 */
export interface PlanningUIState {
  isLoading: boolean;
  error: string | null;
  selectedSegmentId: number | null;
  isModalOpen: boolean;
  modalMode: 'create' | 'edit';
}

/**
 * Modal form data
 */
export interface SegmentFormData {
  location: string;
  scheduledDate: string;
  scheduledTime: string;
  anchorType: AnchorType;
  vehicleType: VehicleType;
  tripType: TripType;
  stopoverDurationMinutes: number;
  passengerCount?: number;
  insertPosition: 'before-game' | 'after-game';
  vehicleAssignmentId?: number;
  clubId?: number;
  customLocation?: string;
}

/**
 * Database structure for export/import
 */
export interface PlanningDatabaseStructure {
  planning: MatchdayPlanning;
  segments: PlanningSegment[];
}

/**
 * Vehicle configuration
 */
export interface VehicleConfig {
  type: VehicleType;
  icon: string;
  name: string;
  color: string;
}

/**
 * Anchor configuration
 */
export interface AnchorConfig {
  type: AnchorType;
  icon: string;
  name: string;
  color: string;
  showDuration: boolean;
}

/**
 * Constants for UI configuration
 */
export const VEHICLE_CONFIGS: Record<VehicleType, VehicleConfig> = {
  bus: { type: 'bus', icon: '', name: 'Mannschaftsbus', color: '#28a745' },
  minibus: { type: 'minibus', icon: '', name: 'Transferbus', color: '#ffc107' },
  car: { type: 'car', icon: '', name: 'KitCar', color: '#17a2b8' },
  plane: { type: 'plane', icon: '', name: 'Flugzeug', color: '#3b82f6' },
  train: { type: 'train', icon: '', name: 'Bahn', color: '#8b5cf6' },
  taxi: { type: 'taxi', icon: '', name: 'Taxi', color: '#f59e0b' },
  rentalcar: { type: 'rentalcar', icon: '', name: 'Mietwagen', color: '#0ea5e9' },
  ship: { type: 'ship', icon: '', name: 'Schiff', color: '#0891b2' },
  bicycle: { type: 'bicycle', icon: '', name: 'Fahrrad', color: '#22c55e' },
  onfoot: { type: 'onfoot', icon: '', name: 'Zu Fuß', color: '#64748b' }
};

export const ANCHOR_CONFIGS: Record<AnchorType, AnchorConfig> = {
  departure: { type: 'departure', icon: '', name: 'Abfahrt', color: '#ff9800', showDuration: false },
  arrival: { type: 'arrival', icon: '', name: 'Ankunft', color: '#2196f3', showDuration: false },
  stopover: { type: 'stopover', icon: '', name: 'Aufenthalt', color: '#9c27b0', showDuration: true },
  free: { type: 'free', icon: '', name: 'Freie Kalkulation', color: '#4caf50', showDuration: false },
  main: { type: 'main', icon: '', name: 'Anstoß', color: '#f44336', showDuration: false }
};

export const TRIP_TYPE_CONFIGS = {
  team: { type: 'team' as TripType, icon: '', name: 'Team', color: '#8b4513' },
  empty: { type: 'empty' as TripType, icon: '', name: 'Leer', color: '#6c757d' }
};

/**
 * Info interfaces for related entities
 */
export interface LocalMatchdayInfo {
  id: number;
  name: string;
  location?: string;
  date: string;
  startTime?: string;
  organizer?: string;
}

export interface MatchInfo {
  id: number;
  date: string;
  scheduledDate: string;
  // Nullable for own matches (Testspiel/Training)
  homeClubId?: number;
  awayClubId?: number;
  homeClub?: ClubInfo;
  awayClub?: ClubInfo;
  matchType?: number;
  homeTeamName?: string;
  awayTeamName?: string;
  location?: string;
  matchday: {
    id: number;
    name: string;
    number: number;
    competitionId?: number;
    competition?: {
      id: number;
      name: string;
      shortName?: string;
    };
  };
}

export interface ClubInfo {
  id: number;
  name: string;
  shortName?: string;
  city?: string;
  stadium?: string;
  stadiumAddress?: string;
}

export interface TransportationInfo {
  id: number;
  name: string;
  type: string;
  capacity?: number;
  licenseRequired?: string;
}

export interface TravelerInfo {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  personType: string;
  licenseClasses: string[];
}

export interface PartnerInfo {
  id: number;
  name: string;
  partnerType?: string;
  description?: string;
  isCustomPartner?: boolean;
}

/**
 * Vehicle entity - concrete vehicle (club-owned or partner-owned)
 */
export interface Vehicle {
  id: number;
  partnerId?: number;
  partnerName?: string;
  name: string;
  vehicleType: string;
  description?: string;
  licensePlate?: string;
  capacity?: number;
  brand?: string;
  model?: string;
  year?: number;
  color?: string;
  features?: string;
  requiredLicenseClass?: string;
  imageUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleInfo {
  id: number;
  name: string;
  vehicleType: string;
  capacity?: number;
  licensePlate?: string;
  partnerId?: number;
  partnerName?: string;
}

/**
 * Partner Driver entity - driver employed by a partner
 */
export interface PartnerDriver {
  id: number;
  partnerId: number;
  partnerName?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role?: string;
  phone?: string;
  email?: string;
  licenseClasses?: string;
  notes?: string;
  imageUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerDriverInfo {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  role?: string;
  phone?: string;
  partnerId: number;
  partnerName?: string;
}

/**
 * Vehicle assignment at planning level (roster entry).
 * Segments reference this via vehicleAssignmentId.
 */
export interface VehicleAssignment {
  id: number;
  planningId: number;
  vehicleType: VehicleType;
  vehicleId?: number;
  transportationId?: number;
  partnerId?: number;
  travelerId?: number;
  partnerDriverId?: number;
  driverType?: DriverType;
  displayOrder: number;
  weeklyDrivingHoursOffset: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;

  // Related entity information
  vehicle?: VehicleInfo;
  transportation?: TransportationInfo;
  partner?: PartnerInfo;
  traveler?: TravelerInfo;
  partnerDriver?: PartnerDriverInfo;
}

export interface CreateVehicleAssignmentRequest {
  planningId: number;
  vehicleType: VehicleType;
  vehicleId?: number;
  transportationId?: number;
  partnerId?: number;
  travelerId?: number;
  partnerDriverId?: number;
  driverType?: DriverType;
  displayOrder?: number;
  weeklyDrivingHoursOffset?: number;
  notes?: string;
}

export interface UpdateVehicleAssignmentRequest {
  vehicleType?: VehicleType;
  vehicleId?: number;
  transportationId?: number;
  partnerId?: number;
  travelerId?: number;
  partnerDriverId?: number;
  driverType?: DriverType;
  displayOrder?: number;
  weeklyDrivingHoursOffset?: number;
  notes?: string;
}

/**
 * Staff role for planning assignments
 */
export enum StaffRole {
  Physio = 0,
  Arzt = 1,
  Athletik = 2
}

/**
 * Staff assignment at planning level
 */
export interface StaffAssignment {
  id: number;
  planningId: number;
  travelerId: number;
  role: StaffRole;
  displayOrder: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;

  // Related entity information
  traveler?: TravelerInfo;
}

export interface CreateStaffAssignmentRequest {
  planningId: number;
  travelerId: number;
  role: StaffRole;
  displayOrder?: number;
  notes?: string;
}

export interface UpdateStaffAssignmentRequest {
  travelerId?: number;
  role?: StaffRole;
  displayOrder?: number;
  notes?: string;
}


// ============================================================
// Template / Favorite Types
// ============================================================

export interface SegmentTemplate {
  id: number;
  name: string;
  isFavorite: boolean;
  planningTemplateId: number | null;
  offsetMinutes: number;
  location: string;
  description: string | null;
  anchorType: AnchorType;
  vehicleType: VehicleType;
  tripType: TripType;
  stopoverDurationMinutes: number | null;
  customLocation: string | null;
  sequenceOrder: number;
  createdAt: string;
}

export interface PlanningTemplate {
  id: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  segments: SegmentTemplate[];
}

export interface SaveSegmentFavoriteRequest {
  name: string;
  segmentId: number;
  planningId: number;
}

export interface ApplyFavoriteRequest {
  segmentTemplateId: number;
  targetPlanningId: number;
  insertPosition: string;
}

export interface CreatePlanningTemplateRequest {
  name: string;
  description?: string;
  sourcePlanningId: number;
}

export interface ApplyTemplateRequest {
  planningTemplateId: number;
  targetPlanningId: number;
}

export interface CopySegmentsFromPlanningRequest {
  sourcePlanningId: number;
  targetPlanningId: number;
}